// useVesselNamingText.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TDataSource } from './SvgTextRender';
import { BufferPoolItem, SurfaceContext, VRContext } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { getBranchType } from './Surface3DUtilities';
import { getActorNameByAnalysisType } from 'config/analysisConfig';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import vtkCoordinate from '@kitware/vtk.js/Rendering/Core/Coordinate';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import { configStore } from 'config/configStore';
import { TaviParsedData } from '@features/analysis/rendering/Panels/Renderers/taviDataUtils';
import {
  FFR_ARTERY_NAME,
  FFR_ARTERY_TYPE,
  FFRNames,
  getFFRArteryTypes,
  loadKeys,
  nodeNames,
} from 'config/render3DConfig';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';

type SvgData = {
  centerLineDataSource: TDataSource[];
  color: string;
};

type params = {
  context: React.MutableRefObject<SurfaceContext | VRContext | null>;
  pool: BufferPoolItem | undefined;
  lowQuality: boolean;
};

type FFRJsonData = {
  vecSelectedNodes?: number[];
  rcaFFR?: number;
  lcxFFR?: number;
  ladFFR?: number;
  [key: string]: unknown;
};

const isFFRJsonData = (data: unknown): data is FFRJsonData => {
  return (
    !!data &&
    typeof data === 'object' &&
    !(data instanceof ArrayBuffer) &&
    !ArrayBuffer.isView(data)
  );
};

const REQUEST_CENTERLINE_KEYS = [
  'fcx',
  'fcy',
  'fcz',
  'centerline',
  'newType',
  'parameter',
] as const;


const REQUEST_TAVI_KEYS = [
  'tavi.annulus',
  'tavi.sinotubularjunction',
  'tavi.sinus',
  'tavi.parsed',
  'parameter',
  'segmentationVolumeMapper',
] as const;

const CALCIUM_SCORE_BRANCH_NAMES = new Set(['RCA', 'LAD', 'LCX', 'LM']);

function getDisplayValueSafe(
  renderer: vtkRenderer,
  position: [number, number, number],
) {
  try {
    const coord = vtkCoordinate.newInstance();
    coord.setCoordinateSystemToWorld();
    coord.setValue(position);
    return coord.getComputedDisplayValue(renderer);
  } catch {
    return undefined;
  }
}

export const nodeToNameMap = {
  [nodeNames.nodeAnnulus]: 'Annulus',
  [nodeNames.nodeSinotubular]: 'Sinotubular Junction',
  [nodeNames.nodeSihus]: 'Sinus of Valsalva',
} as const;

type NodeToNameKey = keyof typeof nodeToNameMap;

function getFFRLabelItems(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  pool: BufferPoolItem | undefined
) {
  const ct_ffr = pool?.ct_ffr.data;
  if (!isFFRJsonData(ct_ffr)) return [];
  const vecSelectedNodes = ct_ffr.vecSelectedNodes;
  const polyData = pool?.FFR_centerline.data as vtkPolyData;
  if(!polyData) return [];
  const ffrs = polyData
    .getPointData()
    .getArrayByName('FFR_Smoothing')
    ?.getData();
  const r0x1l2s = polyData.getCellData().getArrayByName('R0X1L2')?.getData();

  if (!polyData || !vecSelectedNodes?.length || !ffrs || !r0x1l2s) return [];


  const points = polyData.getPoints()?.getData();
  if(!points) return [];
  console.log('돕니다.')

  const ffrValues = getFFRArteryTypes().map((key) => ct_ffr?.[key]);

  const isLCA = context.current?.actorIds[FFRNames.main_lca]?.getVisibility();
  const isRCA = context.current?.actorIds[FFRNames.main_rca]?.getVisibility();

  return vecSelectedNodes.flatMap((id, index) => {
    const pointOffset = id * 3;
    if (pointOffset + 2 >= points.length) return [];
    const labelValue = ffrs[id] === undefined ? ffrValues[index] : ffrs[id];
    const cellIds = polyData.getPointCells(id);
    const firstCellId = cellIds?.[0];
    const type = firstCellId !== undefined ? r0x1l2s[firstCellId] : undefined;
    const arteryType = type !== undefined ? FFR_ARTERY_NAME[type] : undefined;
    const isVisible =
      type === FFR_ARTERY_TYPE.RCA ? isRCA !== false : type !== FFR_ARTERY_TYPE.RCA ? isLCA !== false : true;

    return [
      {
        id: index,
        name: typeof labelValue === 'number' ? labelValue.toFixed(2) : `${id}`,
        position: [
          points[pointOffset],
          points[pointOffset + 1],
          points[pointOffset + 2],
        ] as [number, number, number],
        textAnchor: 'start' as const,
        type: arteryType,
        isVisible,
      },
    ];
  });
}

export function setTextBoxPositions(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  dataSource:{
      id: number;
      name: string;
      position: [number, number, number];
    }[],
  setSvgData: React.Dispatch<React.SetStateAction<{
    centerLineDataSource: {
      id: number;
      name: string;
      position: [number, number, number];
    }[];
    color: string;
  }>>
) {
  if (!context.current) return;
  const { renderer } = context.current;
  const dpr = window.devicePixelRatio || 1;

  const positionsById = new Map(
    dataSource.flatMap((item) => {
      const displayValue = getDisplayValueSafe(renderer, item.position);
      if (!displayValue) return [];
      const [x, y] = displayValue;
      return [
        [
          item.id,
          {
            startX: x / dpr,
            startY: y / dpr,
          },
        ] as const,
      ];
    }),
  );

  setSvgData((prev) => ({
    ...prev,
    centerLineDataSource: prev.centerLineDataSource.map((item) => ({
      ...item,
      ...(positionsById.get(item.id) ?? {}),
    })),
  }));
}

export function useLabelNamingText({
  context,
  pool,
  lowQuality,
}: params) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { globalToolState } = useAnalysisPage();
  const { selectedSeriesId } = useBrowserPage();
  const { analysisType, fullscreenPanelComponentKey } = usePanelContainer();
  const actorType = getActorNameByAnalysisType(analysisType);
  const isDisplayType =
    actorType === 'coronary' ||
    (analysisType === 'tavi' && actorType === 'aorta') ||
    (analysisType === 'FFR' && actorType === 'aorta');

  const isCoronaryDataReady = REQUEST_CENTERLINE_KEYS.every((key) => {
    const result = pool?.[key];
    return result?.loaded === true;
  });
  const taviAortaId = configStore.get().tavi_aorta_id;
  const centerData = pool?.centerData.data as Record<number, any> | undefined;
  const isTaviDataReady =
    REQUEST_TAVI_KEYS.every((key) => {
      const result = pool?.[key];
      return result?.loaded === true;
    }) && !!centerData?.[taviAortaId];

  const isFFRDataReady = pool?.FFR_centerline.loaded;
  const isNamingDataReady =
    !!pool &&
    !!context.current &&
    isDisplayType &&
    (analysisType === 'FFR'
      ? isFFRDataReady
      : analysisType === 'tavi'
        ? isTaviDataReady
        : isCoronaryDataReady);


  const [svgData, setSvgData] = useState<SvgData>({
    centerLineDataSource: [],
    color: 'white',
  });

  const handleDelete = useCallback(
    (id: number) => {
      setSvgData((prev) => ({
        ...prev,
        centerLineDataSource: prev.centerLineDataSource.filter(
          (item) => item.id !== id,
        ),
      }));
    },
    [setSvgData],
  );

  const handleClearFFRPoints = () => {
    setSvgData((prev) => ({
      ...prev,
      centerLineDataSource: [],
    }));
  };

  const handlehideFFRPoints = (isRCA: boolean, visible: boolean) => {
    setSvgData((prev) => ({
      ...prev,
      centerLineDataSource: prev.centerLineDataSource.map((item) =>
        (isRCA ? item.type === FFR_ARTERY_NAME[0] : item.type !== FFR_ARTERY_NAME[0])
          ? { ...item, isVisible: visible }
          : item,
      ),
    }));
  };

  const handleResetDefaultPoints = () => {
    const nextItems: TDataSource[] = [];
    nextItems.push(...getFFRLabelItems(context,pool));
    setSvgData((prev) => ({
      ...prev,
      centerLineDataSource: nextItems,
    }));
  };

  const handleFFRPickPoints = (
    key: string,
    id: number,
    position: [number, number, number],
    branchId:number,
    val?: number,
  ) => {
    const addLabel = (name: string, type?: string) => {
      setSvgData((prev) => ({
        ...prev,
        centerLineDataSource: [
          ...prev.centerLineDataSource,
          {
            id: Math.max(-1, ...prev.centerLineDataSource.map((i) => i.id)) + 1,
            name,
            position,
            textAnchor: 'start',
            type,
            isVisible:true,
          },
        ],
      }));
    };

    const removeTempPoint = (id: number) => {
      setSvgData((prev) => ({
        ...prev,
        centerLineDataSource: prev.centerLineDataSource.filter(
          (item) => item.id !== id,
        ),
      }));
    };

    if (id === -1) {
      removeTempPoint(-1);
      return;
    }

    if (val !== undefined) {
      setSvgData((prev) => {
        const idx = prev.centerLineDataSource.findIndex((i) => i.id === -1);

        if (idx !== -1) {
          const updated = [...prev.centerLineDataSource];
          updated[idx] = {
            ...updated[idx],
            name: val.toFixed(2),
            position,
          };
          return { ...prev, centerLineDataSource: updated };
        }

        return {
          ...prev,
          centerLineDataSource: [
            ...prev.centerLineDataSource,
            {
              id: -1,
              name: val.toFixed(2),
              position,
              textAnchor: 'start',
            },
          ],
        };
      });
      return;
    }

    // 일반 포인트 처리
    if (!context.current) return;

    const actor = context.current.actorIds[key];
    if (!actor) return;

    const polyData = actor.getMapper()?.getInputData() as vtkPolyData;


  const pointData = polyData.getPointData();
  if (!pointData) return;

    const ffrs = polyData
      ?.getPointData()
      ?.getArrayByName('FFR_Smoothing')
      ?.getData();

    const centline = pool?.FFR_centerline.data as vtkPolyData;
    const r0x1l2s = centline.getCellData().getArrayByName('R0X1L2')?.getData();

    if (!ffrs || !r0x1l2s) return;

    const type = branchId !== undefined ? r0x1l2s[branchId] : undefined;
    const arteryType = type !== undefined ? FFR_ARTERY_NAME[type] : undefined;
    const labelValue = ffrs[id];
    if (labelValue === undefined) return;
    addLabel(labelValue.toFixed(2), arteryType);
  };

  // Update vessel naming text box positions.
  const updateTextBoxPositions = useCallback(() => {
    if (!context.current) return;
    setTextBoxPositions(context, svgData.centerLineDataSource, setSvgData);
  }, [svgData.centerLineDataSource]);

  useEffect(() => {
    if (!context.current) return;
    const camera = context.current.renderer.getActiveCamera();
    const subscription = camera.onModified(updateTextBoxPositions);
    return () => subscription?.unsubscribe?.();
  }, [updateTextBoxPositions]);

  useEffect(() => {
    if (!context.current) return;
    if (!selectedSeriesId || !isNamingDataReady) return;
    const nextItems: TDataSource[] = [];
    if (analysisType === 'tavi') {
      const aortaCenterData = pool.centerData.data as
        | Record<number, any>
        | undefined;

      const centerline = aortaCenterData?.[taviAortaId];
      if (!centerline) return;

      const parsed = pool?.['tavi.parsed']?.data as TaviParsedData | null;
      if (!parsed) {
        console.log('parsed 없음');
        return;
      }

      const rawPoints = centerline?.getPoints?.()?.getData?.() as
        | ArrayLike<number>
        | undefined;

      if (!rawPoints || rawPoints.length < 3) {
        setSvgData((prev) => ({ ...prev, centerLineDataSource: [] }));
        return;
      }

      const taviCenterKeys = [
        'annulus',
        'sinus',
        'sinotubularjunction',
      ] as const;
      type TaviCenterKey = (typeof taviCenterKeys)[number];

      const keyNameByTaviCenterKey: Record<TaviCenterKey, loadKeys> = {
        annulus: nodeNames.nodeAnnulus,
        sinus: nodeNames.nodeSihus,
        sinotubularjunction: nodeNames.nodeSinotubular,
      };

      let n = 0;
      for (const key of taviCenterKeys) {
        const centerIdx = parsed[key]?.centerIdx;
        if (centerIdx == null || centerIdx < 0) {
          console.warn('invalid centerIdx', key, centerIdx);
          continue;
        }

        let point = centerline.getPoints().getPoint(centerIdx) as [
          number,
          number,
          number,
        ];
        if (!point) {
          console.warn('point 없음', key, centerIdx);
          continue;
        }

        const keyName = keyNameByTaviCenterKey[key] as NodeToNameKey;
        // const actor = actorIds[keyName] as vtkActor;

        // if (actor) {
        //   const meta = getCircleMeta(actor);
        //   const points = meta?.source.getOutputData().getPoints();
        //   const numberOfPoints = points?.getNumberOfPoints();
        //   const startPoint = points.getPoint(0);
        //   point = startPoint;
        // }

        nextItems.push({
          id: n,
          name: nodeToNameMap[keyName],
          position: [-point[0], point[1], point[2]] as [number, number, number],
          textAnchor: 'start',
        });
        n++;
      }
    } else if (analysisType === 'FFR') {
      nextItems.push(...getFFRLabelItems(context, pool));
    } else if (actorType === 'coronary') {
      const results = getBranchType(selectedSeriesId, pool, lowQuality);
      if (!results || results.length === 0) return;
      nextItems.push(
        ...results.map((res, i) => ({
          id: i,
          name: res.name,
          position: res.world as [number, number, number],
        })),
      );
    }
    setSvgData((prev) => ({
      ...prev,
      centerLineDataSource: nextItems,
    }));
  }, [isNamingDataReady]);

  const canRenderSvgText = useMemo(() => {
    return (
      globalToolState?.toggles.ToggleAnnotations &&
      isNamingDataReady &&
      ['VR', 'Surface3D', 'Empty', undefined].includes(
        fullscreenPanelComponentKey, //이것은 다른뷰가 확대햇을때 텍스트가 같이 나오는경우를 방지
      ) &&
      svgData.centerLineDataSource.length > 0
    );
  }, [
    globalToolState?.toggles.ToggleAnnotations,
    isNamingDataReady,
    fullscreenPanelComponentKey,
    svgData.centerLineDataSource.length,
  ]);

  const filteredCenterlineItems = useMemo(() => {
    if (analysisType !== 'calciumScore') return svgData.centerLineDataSource;
    return svgData.centerLineDataSource.filter((item) =>
      CALCIUM_SCORE_BRANCH_NAMES.has(item.name),
    );
  }, [analysisType, svgData.centerLineDataSource]);

  return {
    svgRef,
    svgData,
    handleFFRPickPoints,
    updateTextBoxPositions,
    handleDelete,
    handleClearFFRPoints,
    handlehideFFRPoints,
    handleResetDefaultPoints,
    isNamingDataReady,
    canRenderSvgText,
    filteredCenterlineItems,
  };
}
